import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import {
  Building2,
  Users,
  DollarSign,
  Activity,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  AlertOctagon,
  CheckCircle2,
  Clock,
  RefreshCw,
  Search,
  MessageSquare,
  Bell,
  CreditCard,
  Settings,
  Shield
} from 'lucide-react';
import OwnerAdminPaymentsCard from './OwnerAdminPaymentsCard';
import RevenueTrendChart from './RevenueTrendChart';
import MRRMilestoneBanner from './MRRMilestoneBanner';
import StuckInvoiceAlert from './StuckInvoiceAlert';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Metric Card Component
const MetricCard = ({ title, value, change, icon: Icon, color = 'emerald', className = '', ...rest }) => (
  <Card className={`bg-card border-white/15 ${className}`} {...rest}>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-3xl font-bold text-white mt-1">{value}</p>
          {change !== undefined && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${typeof change === 'number' && change >= 0 ? 'text-emerald-400' : typeof change === 'number' ? 'text-red-400' : 'text-muted-foreground'}`}>
              {typeof change === 'number' ? (
                change >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />
              ) : null}
              <span>{typeof change === 'number' ? (change >= 0 ? '+' : '') + change : change}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-full ${color === 'emerald' ? 'bg-emerald-500/10' : color === 'blue' ? 'bg-blue-500/10' : 'bg-violet-500/10'}`}>
          <Icon className={`h-6 w-6 ${color === 'emerald' ? 'text-emerald-400' : color === 'blue' ? 'text-blue-400' : 'text-violet-400'}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Health Card Component
const HealthCard = ({ title, status, details }) => (
  <Card className="bg-card border-white/15">
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <div className="flex items-center gap-2 mt-2">
            {status === 'healthy' ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            ) : status === 'degraded' ? (
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-red-400" />
            )}
            <span className={`font-medium ${
              status === 'healthy' ? 'text-emerald-400' : 
              status === 'degraded' ? 'text-yellow-400' : 'text-red-400'
            }`}>
              {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
            </span>
          </div>
          {details && <p className="text-xs text-muted-foreground mt-1">{details}</p>}
        </div>
        <Activity className="h-6 w-6 text-muted-foreground" />
      </div>
    </CardContent>
  </Card>
);

export default function OwnerAdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, activityRes] = await Promise.all([
        fetch(`${API_URL}/api/owner-admin/dashboard/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/owner-admin/dashboard/activity?limit=10`, { credentials: 'include' })
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (activityRes.ok) {
        setActivity(await activityRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    if (action.includes('login')) return Users;
    if (action.includes('customer')) return Building2;
    if (action.includes('impersonate')) return Search;
    if (action.includes('ticket')) return MessageSquare;
    return Activity;
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="bg-card border-white/15">
              <CardContent className="pt-6">
                <Skeleton className="h-4 w-24 bg-white/10" />
                <Skeleton className="h-8 w-16 mt-2 bg-white/10" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* MRR Milestone banner — confetti when we cross $1k/$5k/$10k */}
      <MRRMilestoneBanner />

      {/* Stripe paid but invoice still unpaid > 1h (missed webhook) */}
      <StuckInvoiceAlert />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-muted-foreground">Overview of your SaaS platform</p>
        </div>
        <Button variant="outline" onClick={fetchDashboardData} className="border-white/20 text-muted-foreground/80">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="MRR"
          value={`$${(stats?.revenue?.mrr_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          change={stats?.revenue?.arr_usd ? `$${stats.revenue.arr_usd.toLocaleString()} ARR` : undefined}
          icon={CreditCard}
          color="emerald"
          data-testid="dashboard-mrr-card"
        />
        <MetricCard
          title="Paying Customers"
          value={stats?.customers?.paying || 0}
          change={stats?.customers?.trialing ? `${stats.customers.trialing} trialing` : undefined}
          icon={TrendingUp}
          color="violet"
          data-testid="dashboard-paying-card"
        />
        <MetricCard
          title="Total Customers"
          value={stats?.customers?.total || 0}
          change={`${stats?.customers?.paying || 0} paying · ${stats?.customers?.trialing || 0} trialing · ${stats?.customers?.test || 0} test${stats?.customers?.archived ? ` · ${stats.customers.archived} archived` : ''} — click to drill down`}
          icon={Building2}
          color="blue"
          className="cursor-pointer hover:border-blue-400/50 transition-colors"
          onClick={() => navigate('/owner-admin/customers?include_test=1')}
          data-testid="dashboard-total-card"
        />
        <HealthCard
          title="System Status"
          status={stats?.health?.status || 'unknown'}
          details={`${stats?.health?.errors_24h || 0} errors in 24h`}
        />
      </div>

      {/* Phase 2: Platform Payment Revenue (owner only) */}
      <OwnerAdminPaymentsCard />

      {/* Revenue Trend — nightly MRR snapshots as a growth chart */}
      <RevenueTrendChart />

      {/* Quick Actions */}
      <Card className="bg-card border-white/15">
        <CardHeader>
          <CardTitle className="text-white">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button 
              variant="outline" 
              className="border-white/20 text-muted-foreground/80 hover:bg-white/10"
              onClick={() => navigate('/owner-admin/users')}
            >
              <Search className="h-4 w-4 mr-2" />
              Search Users
            </Button>
            <Button 
              variant="outline" 
              className="border-white/20 text-muted-foreground/80 hover:bg-white/10"
              onClick={() => navigate('/owner-admin/system')}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              View Errors
              {stats?.health?.errors_24h > 0 && (
                <Badge className="ml-2 bg-red-500/20 text-red-400">{stats.health.errors_24h}</Badge>
              )}
            </Button>
            <Button 
              variant="outline" 
              className="border-white/20 text-muted-foreground/80 hover:bg-white/10"
              onClick={() => navigate('/owner-admin/support')}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Support Queue
            </Button>
            <Button 
              variant="outline" 
              className="border-white/20 text-muted-foreground/80 hover:bg-white/10"
              onClick={() => navigate('/owner-admin/customers')}
            >
              <Building2 className="h-4 w-4 mr-2" />
              View Customers
            </Button>
            <Button
              variant="outline"
              className="border-orange-400/30 text-orange-300 hover:bg-orange-500/10"
              onClick={() => navigate('/owner-admin/unpaid-sweep')}
              data-testid="dashboard-unpaid-sweep-btn"
            >
              <AlertOctagon className="h-4 w-4 mr-2" />
              Unpaid Sweep
            </Button>
            <Button 
              variant="outline" 
              className="border-white/20 text-muted-foreground/80 hover:bg-white/10"
              onClick={() => navigate('/owner-admin/billing')}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Billing
            </Button>
            <Button 
              variant="outline" 
              className="border-white/20 text-muted-foreground/80 hover:bg-white/10"
              onClick={() => navigate('/owner-admin/staff')}
            >
              <Shield className="h-4 w-4 mr-2" />
              Manage Staff
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card className="bg-card border-white/15">
        <CardHeader>
          <CardTitle className="text-white">Recent Activity</CardTitle>
          <CardDescription className="text-muted-foreground">Latest admin actions</CardDescription>
        </CardHeader>
        <CardContent>
          {activity.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No recent activity</p>
          ) : (
            <div className="space-y-4">
              {activity.map((item, index) => {
                const Icon = getActionIcon(item.action);
                return (
                  <div key={index} className="flex items-center gap-4 p-3 rounded-lg bg-white/[0.07]">
                    <div className="p-2 rounded-full bg-white/10">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white">
                        <span className="font-medium">{item.admin_email}</span>
                        {' '}<span className="text-muted-foreground">{item.action.replace(/_/g, ' ')}</span>
                      </p>
                      {item.target_type && (
                        <p className="text-xs text-muted-foreground">
                          {item.target_type}: {item.target_id}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {formatTime(item.timestamp)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
